import React, {useEffect, useMemo, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import VendorPaymentMethodsApi from '../../Api/vendorPaymentMethods'
import {
  CREDENTIAL_FIELDS_BY_FACTORY,
  GATEWAY_CATALOG,
  getGatewayCatalog,
  PAYMENT_COLORS,
} from './constants'
import {
  AvailableCatalogGateway,
  GatewayCardModel,
  GatewayEnvironment,
  VendorPaymentMethod,
} from './types'

type Mode = 'add' | 'manage' | 'connect'

type Props = {
  open: boolean
  mode: Mode
  card?: GatewayCardModel | null
  availableGateways: AvailableCatalogGateway[]
  configured: VendorPaymentMethod[]
  onSuccess: () => void
  onClose: () => void
}

export default function GatewayModal({
  open,
  mode,
  card,
  availableGateways,
  configured,
  onSuccess,
  onClose,
}: Props) {
  const {t} = useTranslation()
  const [factoryName, setFactoryName] = useState('')
  const [catalogId, setCatalogId] = useState(0)
  const [environment, setEnvironment] = useState<GatewayEnvironment>('sandbox')
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [testState, setTestState] = useState<'idle' | 'testing' | 'valid' | 'invalid'>('idle')
  const [testMessage, setTestMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [showLiveWarning, setShowLiveWarning] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editRowId, setEditRowId] = useState<number | undefined>()

  const selectable = useMemo(() => {
    return availableGateways.filter((gateway) => {
      if (!gateway.is_enabled) return false
      if (mode === 'manage' || mode === 'connect') {
        return gateway.factory_name === (card?.factory_name || factoryName)
      }
      const envs = configured
        .filter((item) => item.factory_name === gateway.factory_name)
        .map((item) => item.environment)
      return !(envs.includes('sandbox') && envs.includes('live'))
    })
  }, [availableGateways, configured, mode, card, factoryName])

  useEffect(() => {
    if (!open) return

    if ((mode === 'manage' || mode === 'connect') && card) {
      setFactoryName(card.factory_name)
      setCatalogId(card.catalog_id || 0)
      const preferLive = Boolean(card.environments.live)
      const preferSandbox = Boolean(card.environments.sandbox)
      const env: GatewayEnvironment =
        mode === 'connect'
          ? preferSandbox && !preferLive
            ? 'live'
            : preferLive && !preferSandbox
              ? 'sandbox'
              : 'sandbox'
          : card.environments.live?.is_active
            ? 'live'
            : card.environments.sandbox
              ? 'sandbox'
              : 'live'
      setEnvironment(env)
      const row = configured.find(
        (item) => item.factory_name === card.factory_name && item.environment === env
      )
      setEditRowId(mode === 'manage' ? row?.id : undefined)
      setCredentials({})
      setTestState('idle')
      setTestMessage('')
      setErrors({})
      setShowLiveWarning(false)
      return
    }

    setFactoryName('')
    setCatalogId(0)
    setEnvironment('sandbox')
    setCredentials({})
    setTestState('idle')
    setTestMessage('')
    setErrors({})
    setEditRowId(undefined)
    setShowLiveWarning(false)
  }, [open, mode, card, configured])

  useEffect(() => {
    if (!open || mode !== 'manage' || !factoryName) return
    const row = configured.find(
      (item) => item.factory_name === factoryName && item.environment === environment
    )
    setEditRowId(row?.id)
  }, [environment, factoryName, configured, open, mode])

  if (!open) {
    return null
  }

  const catalog = getGatewayCatalog(factoryName)
  const fields = CREDENTIAL_FIELDS_BY_FACTORY[factoryName] || []
  const title =
    mode === 'manage' && catalog
      ? `${t('payment.modal.title_manage') as string} ${t(catalog.nameKey) as string}`
      : (t('payment.modal.title_add') as string)

  const onSelectGateway = (factory: string) => {
    const gateway = selectable.find((item) => item.factory_name === factory)
    setFactoryName(factory)
    setCatalogId(gateway?.id || 0)
    setCredentials({})
    setTestState('idle')
    setTestMessage('')
  }

  const onEnvironmentChange = (value: GatewayEnvironment) => {
    if (value === 'live') {
      setShowLiveWarning(true)
      return
    }
    setEnvironment(value)
    setTestState('idle')
  }

  const validateFields = (): boolean => {
    const next: Record<string, string> = {}
    if (!factoryName) {
      next.factory_name = t('payment.modal.select_gateway') as string
    }
    const isEditExisting = mode === 'manage' && Boolean(editRowId)
    if (!isEditExisting) {
      fields.forEach((field) => {
        if (!credentials[field.key]?.trim()) {
          next[field.key] = t('payment.field_required') as string
        }
      })
      const duplicate = configured.some(
        (item) => item.factory_name === factoryName && item.environment === environment
      )
      if (duplicate) {
        next.environment = t('payment.environment_exists') as string
      }
    } else {
      const hasAny = Object.values(credentials).some((value) => value?.trim())
      if (hasAny) {
        fields.forEach((field) => {
          if (!credentials[field.key]?.trim()) {
            next[field.key] = t('payment.field_required') as string
          }
        })
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const runTest = () => {
    if (!validateFields()) return
    setTestState('testing')
    setTestMessage('')

    const payload = {
      factory_name: factoryName,
      environment,
      country_payment_gateway_id: catalogId || undefined,
      keys: credentials,
    }

    const credentialsChanged = Object.values(credentials).some((value) => value?.trim())
    if (mode === 'manage' && editRowId && !credentialsChanged) {
      VendorPaymentMethodsApi.testById(
        editRowId,
        (data: any) => {
          if (data?.ErrorStatus || data?.msgErr) {
            setTestState('invalid')
            setTestMessage(
              data?.msgErr || data?.message || (t('payment.errors.test_failed') as string)
            )
            return
          }
          const valid = data?.valid !== false && data?.status !== false
          setTestState(valid ? 'valid' : 'invalid')
          setTestMessage(
            (data?.message as string) ||
              (valid
                ? (t('payment.success.test_passed') as string)
                : (t('payment.errors.test_failed') as string))
          )
        },
        () => undefined
      )
      return
    }

    VendorPaymentMethodsApi.testCredentials(
      payload,
      (data: any) => {
        if (data?.ErrorStatus || data?.msgErr) {
          setTestState('invalid')
          setTestMessage(
            data?.msgErr || data?.message || (t('payment.errors.test_failed') as string)
          )
          return
        }
        const valid = data?.valid !== false && data?.status !== false
        setTestState(valid ? 'valid' : 'invalid')
        setTestMessage(
          (data?.message as string) ||
            (valid
              ? (t('payment.success.test_passed') as string)
              : (t('payment.errors.test_failed') as string))
        )
      },
      (loading: boolean) => {
        if (loading) setTestState('testing')
      }
    )
  }

  const save = () => {
    if (!validateFields()) return
    const credentialsChanged = Object.values(credentials).some((value) => value?.trim())
    const isCreate = !(mode === 'manage' && editRowId)

    if (isCreate && testState !== 'valid') {
      setErrors({test: t('payment.must_validate') as string})
      return
    }
    if (!isCreate && credentialsChanged && testState !== 'valid') {
      setErrors({test: t('payment.must_validate') as string})
      return
    }

    const payload = {
      factory_name: factoryName,
      environment,
      country_payment_gateway_id: catalogId || undefined,
      keys: credentials,
      is_enabled: true,
    }

    if (!isCreate && editRowId) {
      const updatePayload: {keys?: Record<string, string>} = {}
      if (credentialsChanged) {
        updatePayload.keys = credentials
      }
      VendorPaymentMethodsApi.update(
        editRowId,
        updatePayload,
        (data: any) => {
          if (data?.ErrorStatus || data?.msgErr) {
            toast.error(
              `${t('payment.errors.save_failed') as string}: ${
                data?.msgErr || data?.message || ''
              }`
            )
            return
          }
          toast.success(t('payment.success.updated') as string)
          onSuccess()
        },
        setIsSaving
      )
      return
    }

    VendorPaymentMethodsApi.create(
      payload,
      (data: any) => {
        if (data?.ErrorStatus || data?.msgErr) {
          toast.error(
            `${t('payment.errors.save_failed') as string}: ${data?.msgErr || data?.message || ''}`
          )
          return
        }
        toast.success(t('payment.success.added') as string)
        onSuccess()
      },
      setIsSaving
    )
  }

  if (showLiveWarning) {
    return (
      <Box p={{xs: 2, md: 3}}>
        <Alert severity='warning' sx={{mb: 2}}>
          <Typography fontWeight={700}>{t('payment.status.live') as string}</Typography>
          <Typography mt={1}>{t('payment.live_warning') as string}</Typography>
        </Alert>
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          <Button onClick={() => setShowLiveWarning(false)}>
            {t('payment.buttons.cancel') as string}
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={() => {
              setEnvironment('live')
              setShowLiveWarning(false)
              setTestState('idle')
            }}
          >
            {t('payment.understand') as string}
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <Box
      p={{xs: 2, md: 3}}
      sx={{
        maxWidth: 500,
        width: '100%',
        mx: 'auto',
        opacity: 1,
        transition: 'opacity 200ms ease',
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontSize={20} fontWeight={600} color={PAYMENT_COLORS.textPrimary}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='close'>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack gap={2}>
        {mode === 'add' ? (
          <Box>
            <Typography
              fontSize={12}
              fontWeight={600}
              color={PAYMENT_COLORS.textSecondary}
              mb={1}
            >
              {t('payment.modal.select_gateway') as string}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
              }}
            >
              {GATEWAY_CATALOG.filter((item) =>
                selectable.some((g) => g.factory_name === item.factory_name)
              ).map((item) => {
                const selected = factoryName === item.factory_name
                return (
                  <Box
                    key={item.factory_name}
                    onClick={() => onSelectGateway(item.factory_name)}
                    sx={{
                      width: '100%',
                      minHeight: 80,
                      p: 1.5,
                      borderRadius: '8px',
                      border: selected
                        ? `2px solid ${item.brandColor}`
                        : `1px solid ${PAYMENT_COLORS.border}`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      backgroundColor: selected ? `${item.brandColor}08` : PAYMENT_COLORS.surface,
                    }}
                  >
                    <Box
                      component='img'
                      src={item.logo}
                      onError={(e: any) => {
                        e.currentTarget.src = item.logoFallback
                      }}
                      alt={t(item.nameKey) as string}
                      sx={{width: 48, height: 48, objectFit: 'contain'}}
                    />
                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {t(item.nameKey) as string}
                      </Typography>
                      <Typography fontSize={12} color={PAYMENT_COLORS.textSecondary}>
                        {t(item.typeKey) as string}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
            {errors.factory_name ? (
              <Typography color='error' fontSize={12} mt={0.5}>
                {errors.factory_name}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {factoryName ? (
          <>
            <Box>
              <Typography
                fontSize={12}
                fontWeight={600}
                color={PAYMENT_COLORS.textSecondary}
                mb={1}
              >
                {t('payment.modal.select_environment') as string}
              </Typography>
              <RadioGroup
                value={environment}
                onChange={(_, value) => onEnvironmentChange(value as GatewayEnvironment)}
              >
                <FormControlLabel
                  value='sandbox'
                  control={<Radio />}
                  label={t('payment.modal.environment_sandbox_help') as string}
                />
                <FormControlLabel
                  value='live'
                  control={<Radio />}
                  label={t('payment.modal.environment_live_help') as string}
                />
              </RadioGroup>
              <Typography fontSize={11} color={PAYMENT_COLORS.textMuted} mt={0.5}>
                {t('payment.modal.environment_help') as string}
              </Typography>
              {errors.environment ? (
                <Typography color='error' fontSize={12}>
                  {errors.environment}
                </Typography>
              ) : null}
            </Box>

            <Stack gap={1.5}>
              {mode === 'manage' && editRowId ? (
                <Alert severity='info'>{t('payment.credentials_masked') as string}</Alert>
              ) : null}
              {fields.map((field) => (
                <Box key={field.key}>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    color={PAYMENT_COLORS.textSecondary}
                    mb={0.5}
                  >
                    {t(field.labelKey) as string}
                  </Typography>
                  <TextField
                    type={field.inputType || 'text'}
                    value={credentials[field.key] || ''}
                    onChange={(e) => {
                      setCredentials((prev) => ({...prev, [field.key]: e.target.value}))
                      setTestState('idle')
                    }}
                    placeholder={mode === 'manage' && editRowId ? '••••••••••' : undefined}
                    error={Boolean(errors[field.key])}
                    helperText={errors[field.key]}
                    fullWidth
                    autoComplete='off'
                    sx={{
                      '& .MuiInputBase-root': {height: 40},
                      '& .MuiInputBase-input': {px: 1, py: 0.5},
                    }}
                  />
                </Box>
              ))}
            </Stack>

            <Button
              fullWidth
              variant='outlined'
              onClick={runTest}
              disabled={testState === 'testing'}
              sx={{
                height: 40,
                mt: 1,
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'none',
              }}
            >
              {testState === 'testing' ? (
                <CircularProgress size={18} />
              ) : (
                (t('payment.buttons.test_connection') as string)
              )}
            </Button>
            {testState === 'valid' ? (
              <Alert severity='success'>{testMessage}</Alert>
            ) : null}
            {testState === 'invalid' ? (
              <Alert severity='error'>{testMessage}</Alert>
            ) : null}
            {errors.test ? (
              <Typography color='error' fontSize={12}>
                {errors.test}
              </Typography>
            ) : null}
          </>
        ) : null}

        <Stack
          direction={{xs: 'column', sm: 'row'}}
          gap={2}
          mt={1}
          justifyContent='flex-end'
        >
          <Button
            onClick={onClose}
            sx={{
              height: 40,
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              color: PAYMENT_COLORS.textSecondary,
            }}
          >
            {t('payment.buttons.cancel') as string}
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={isSaving || !factoryName}
            sx={{
              height: 40,
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              backgroundColor: PAYMENT_COLORS.primary,
              '&:hover': {backgroundColor: '#244A96'},
            }}
          >
            {isSaving ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              (t('payment.buttons.save') as string)
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
